import { Fragment, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import {
  ChevronRight,
  ListChecks,
  MoreHorizontal,
  Pause,
  Play,
  Settings,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import cronstrue from "cronstrue";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MotionTableRow } from "@/components/ui/motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatDuration, formatRelativeDate } from "@/lib/formatters";
import { getRunStatusColor } from "@/lib/constants";
import { isEmailSyncJob } from "@/lib/email";
import { EmailSyncRunStats } from "@/components/engine/EmailSyncRunStats";
import type { JobConfig } from "@/api/types";

const SHORT_TEXT_MAX = 30;

interface CronDescription {
  shortText: string;
  longText: string;
  isManual: boolean;
}

function describeCron(cron: string | null | undefined): CronDescription {
  if (!cron || !cron.trim()) {
    return { shortText: "Manual", longText: "Manual trigger", isManual: true };
  }
  try {
    const longText = cronstrue.toString(cron, {
      use24HourTimeFormat: false,
      verbose: false,
    });
    const shortText =
      longText.length > SHORT_TEXT_MAX
        ? `${longText.slice(0, SHORT_TEXT_MAX - 1).trimEnd()}…`
        : longText;
    return { shortText, longText, isManual: false };
  } catch {
    const trimmed = cron.trim();
    return { shortText: trimmed, longText: trimmed, isManual: false };
  }
}

interface JobsTableProps {
  jobs: JobConfig[];
  onPause: (job: JobConfig) => void;
  onResume: (job: JobConfig) => void;
  onTrigger: (job: JobConfig) => void;
  onConfigure: (job: JobConfig) => void;
  onViewRuns: (job: JobConfig) => void;
  onRowClick: (job: JobConfig) => void;
  canTrigger?: boolean;
  canPauseResume?: boolean;
  canConfigure?: boolean;
  busyJobId?: string | null;
}

export function JobsTable({
  jobs,
  onPause,
  onResume,
  onTrigger,
  onConfigure,
  onViewRuns,
  onRowClick,
  canTrigger = false,
  canPauseResume = false,
  canConfigure = false,
  busyJobId,
}: JobsTableProps) {
  const columns = useMemo<ColumnDef<JobConfig>[]>(
    () => [
      {
        id: "job",
        header: "Job",
        // Width values are percentages; the render below appends "%".
        size: 35,
        cell: ({ row }) => {
          const job = row.original;
          const dotState = jobDotState(job);
          return (
            <div className="flex items-start gap-2.5">
              <span
                className={cn(
                  "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                  dotState.dot,
                  dotState.pulse && "animate-pulse",
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="break-words text-sm font-medium leading-snug text-slate-900">
                  {job.name}
                </div>
                <div className="mt-0.5 truncate text-[11px] text-slate-400">
                  {job.job_id}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: "schedule",
        header: "Schedule",
        size: 15,
        cell: ({ row }) => {
          const job = row.original;
          const cron = describeCron(job.cron_expression);
          const cell = cron.isManual ? (
            <span className="block truncate text-[13px] text-slate-500">
              Manual
            </span>
          ) : (
            <code className="block truncate font-mono text-[13px] text-slate-500">
              {job.cron_expression}
            </code>
          );
          if (cron.isManual && !job.timezone && !job.next_run_at) {
            return cell;
          }
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="block w-full max-w-full text-left"
                >
                  {cell}
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="start"
                className="max-w-xs space-y-1.5 px-3 py-2 text-xs leading-relaxed"
              >
                <div className="font-medium">{cron.longText}</div>
                {job.timezone && (
                  <div className="text-[11px] text-primary-foreground/70">
                    Timezone: {job.timezone}
                  </div>
                )}
                {job.next_run_at && (
                  <div className="text-[11px] text-primary-foreground/70">
                    Next: {formatRunDateTime(job.next_run_at)}
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        size: 10,
        cell: ({ row }) => {
          const job = row.original;
          const status = derivedStatus(job);
          const colors = getRunStatusColor(status.key);
          const isRunning = status.key === "running";
          return (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium",
                colors.bg,
                colors.text,
              )}
            >
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  colors.dot,
                  isRunning && "animate-pulse",
                )}
              />
              {status.label}
            </span>
          );
        },
      },
      {
        id: "last_run",
        header: "Last run",
        size: 18,
        cell: ({ row }) => {
          const job = row.original;
          if (job.last_run_at == null) {
            return <span className="text-xs text-slate-400">—</span>;
          }
          const lastStatus = job.last_run_status ?? "running";
          const colors = getRunStatusColor(lastStatus);
          const duration = job.last_run?.duration_seconds;
          return (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-600">
                {formatRelativeDate(job.last_run_at)}
                {duration != null && job.last_run?.status !== "running" && (
                  <span className="text-slate-400"> · {formatDuration(duration)}</span>
                )}
              </span>
              <span
                className={cn(
                  "inline-flex h-1.5 w-1.5 rounded-full",
                  colors.dot,
                )}
                aria-label={lastStatus}
                title={lastStatus}
              />
            </div>
          );
        },
      },
      {
        id: "next_run",
        header: "Next",
        size: 14,
        cell: ({ row }) => {
          const job = row.original;
          if (job.paused_until) {
            return (
              <span className="text-xs text-amber-700">
                Paused · {formatRelativeDate(job.paused_until)}
              </span>
            );
          }
          if (!job.is_enabled) {
            return <span className="text-xs text-slate-400">Disabled</span>;
          }
          if (job.blocked_reason) {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="cursor-help text-xs text-amber-700 underline decoration-dotted underline-offset-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Not scheduled
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                  {job.blocked_reason}
                </TooltipContent>
              </Tooltip>
            );
          }
          if (!job.next_run_at) {
            return <span className="text-xs text-slate-400">—</span>;
          }
          return (
            <span className="text-xs text-slate-600">
              {formatRelativeDate(job.next_run_at)}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: () => <div className="text-right sr-only">Actions</div>,
        size: 8,
        cell: ({ row }) => {
          const job = row.original;
          const needsResume = !job.is_enabled || job.paused_until != null;
          const isBusy = busyJobId === job.job_id;
          return (
            <div
              className="flex items-center justify-end gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    disabled={isBusy}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
                    aria-label="Job actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canTrigger && (
                    <DropdownMenuItem onSelect={() => onTrigger(job)}>
                      <Play className="mr-2 h-3.5 w-3.5" />
                      Run now
                    </DropdownMenuItem>
                  )}
                  {canPauseResume &&
                    (needsResume ? (
                      <DropdownMenuItem onSelect={() => onResume(job)}>
                        <Play className="mr-2 h-3.5 w-3.5" />
                        Resume
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onSelect={() => onPause(job)}>
                        <Pause className="mr-2 h-3.5 w-3.5" />
                        Pause
                      </DropdownMenuItem>
                    ))}
                  {canConfigure && (
                    <DropdownMenuItem onSelect={() => onConfigure(job)}>
                      <Settings className="mr-2 h-3.5 w-3.5" />
                      Configure
                    </DropdownMenuItem>
                  )}
                  {(canTrigger || canPauseResume || canConfigure) && (
                    <DropdownMenuSeparator />
                  )}
                  <DropdownMenuItem onSelect={() => onViewRuns(job)}>
                    <ListChecks className="mr-2 h-3.5 w-3.5" />
                    View runs
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <ChevronRight
                aria-hidden
                className="h-4 w-4 text-slate-300 opacity-0 transition-opacity group-hover/row:opacity-100"
              />
            </div>
          );
        },
      },
    ],
    [
      busyJobId,
      onPause,
      onResume,
      onTrigger,
      onConfigure,
      onViewRuns,
      canTrigger,
      canPauseResume,
      canConfigure,
    ],
  );

  const table = useReactTable({
    data: jobs,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <TooltipProvider delayDuration={150}>
      <div className="overflow-hidden rounded-plane bg-white shadow-plane">
        <Table className="w-full table-fixed">
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="border-slate-900/[0.06] hover:bg-transparent">
                {hg.headers.map((h) => (
                  <TableHead
                    key={h.id}
                    style={{
                      width: h.column.columnDef.size
                        ? `${h.column.columnDef.size}%`
                        : undefined,
                    }}
                    className="text-[11px] font-medium uppercase tracking-wider text-slate-500"
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            <AnimatePresence initial={false} mode="popLayout">
              {table.getRowModel().rows.map((row, idx) => {
                const job = row.original;
                const emailSync = isEmailSyncJob(job.job_id);
                return (
                  <Fragment key={row.id}>
                    <MotionTableRow
                      index={idx}
                      onClick={() => onRowClick(job)}
                      className={cn(
                        "group/row cursor-pointer border-slate-900/[0.06] transition-colors duration-150 hover:bg-indigo-500/[0.04] motion-reduce:transition-none",
                        emailSync && "border-b-0",
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className="overflow-hidden py-3 text-sm"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </MotionTableRow>
                    {emailSync && (
                      <TableRow
                        onClick={() => onRowClick(job)}
                        className="cursor-pointer border-slate-900/[0.06] hover:bg-indigo-500/[0.04]"
                      >
                        <TableCell colSpan={columns.length} className="pb-3 pl-9 pt-0">
                          {job.last_run && job.last_run.status !== "skipped" ? (
                            <EmailSyncRunStats
                              stats={job.last_run.stats}
                              error={job.last_run.error}
                              compact
                            />
                          ) : (
                            <span className="text-[11px] text-slate-400">
                              {job.last_run
                                ? [
                                    `Last run skipped${job.last_run.reason ? ` — ${job.last_run.reason}` : ""}`,
                                    job.blocked_reason,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")
                                : job.blocked_reason ?? "No run yet."}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </AnimatePresence>
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}

function formatRunDateTime(value: string): string {
  try {
    return format(parseISO(value), "EEE MMM d 'at' h:mm a");
  } catch {
    return value;
  }
}

function jobDotState(job: JobConfig): { dot: string; pulse: boolean } {
  if (job.last_run_status === "running") {
    return { dot: "bg-blue-500", pulse: true };
  }
  if (job.paused_until != null) return { dot: "bg-amber-400", pulse: false };
  if (!job.is_enabled) return { dot: "bg-slate-300", pulse: false };
  if (job.last_run_status === "succeeded" || job.last_run_status === "success") {
    return { dot: "bg-emerald-500", pulse: false };
  }
  if (job.last_run_status === "failed") {
    return { dot: "bg-rose-500", pulse: false };
  }
  if (job.last_run_status === "partial") {
    return { dot: "bg-orange-500", pulse: false };
  }
  return { dot: "bg-slate-400", pulse: false };
}

interface DerivedStatus {
  key: string;
  label: string;
}

function derivedStatus(job: JobConfig): DerivedStatus {
  const last = job.last_run_status;
  if (last === "running") return { key: "running", label: "Running" };
  if (job.paused_until != null) return { key: "paused", label: "Paused" };
  if (!job.is_enabled) return { key: "default", label: "Disabled" };
  // Accept both "success" and "succeeded" so the badge tracks whichever
  // convention the backend emits.
  if (last === "success" || last === "succeeded") {
    return { key: "success", label: "Success" };
  }
  if (last === "failed") return { key: "failed", label: "Failed" };
  if (last === "partial") return { key: "partial", label: "Partial" };
  if (last === "skipped") return { key: "skipped", label: "Skipped" };
  if (last === "cancelled") return { key: "cancelled", label: "Cancelled" };
  return { key: "default", label: "Idle" };
}

export function JobsTableSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="overflow-hidden rounded-plane bg-white shadow-plane">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b border-slate-900/[0.06] px-4 py-3 last:border-b-0"
        >
          <div className="h-2 w-2 animate-pulse rounded-full bg-slate-200" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-40 animate-pulse rounded bg-slate-200" />
            <div className="h-2 w-24 animate-pulse rounded bg-slate-100" />
          </div>
          <div className="h-5 w-16 animate-pulse rounded-full bg-slate-100" />
          <div className="h-3 w-20 animate-pulse rounded bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

